import { Injectable, inject } from '@angular/core';
import { HttpService } from '../../services/http.service';
import { PatientService } from '../../services/patient/patient.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

interface LocationFilter {
  offset: number;
  limit: number;
  keyword: string;
  region_id?: number;
  province_id?: number;
  city_id?: number;
}

interface LocationResponse {
  value: string;
  data: any[];
  count: number;
  status: number;
}

export interface PatientRegistrationRequest {
  firstname: string;
  lastname: string;
  middlename?: string;
  suffix?: string;
  birth_date?: string;
  government_id_number?: string;
  mobile_number: string;
  email: string;
  region?: string;
  city?: string;
  // patient_id removed - will be derived from authentication context
}

export interface PrescriptionRequest {
  type?: string;
  // patient_id removed - will be derived from authentication context
  prescribed_product_id?: number;
  [key: string]: any;
}

export interface RegistrationUpsertPayload {
  patient_registration_request: PatientRegistrationRequest;
  prescription_request: PrescriptionRequest;
  prescription_request_files?: any[];
}

export interface UpsertRequest {
  registration_code?: string;
  url_code?: string;
  registration_mode?: string;
  data: RegistrationUpsertPayload;
}

export interface RegistrationResponse {
  value: string;
  data?: any;
  message?: string;
  status: number;
}

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  private http = inject(HttpService);
  private patientService = inject(PatientService);
  private httpClient = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getRegions(filter: LocationFilter): Observable<LocationResponse> {
    return this.http.post<LocationResponse>(`${this.apiUrl}/paacap_patient_locations/region`, { web_filter: filter });
  }

  getProvinces(filter: LocationFilter): Observable<LocationResponse> {
    return this.http.post<LocationResponse>(`${this.apiUrl}/paacap_patient_locations/province`, { web_filter: filter });
  }

  getCities(filter: LocationFilter): Observable<LocationResponse> {
    return this.http.post<LocationResponse>(`${this.apiUrl}/paacap_patient_locations/city`, { web_filter: filter });
  }

  getBarangays(filter: LocationFilter): Observable<LocationResponse> {
    return this.http.post<LocationResponse>(`${this.apiUrl}/paacap_patient_locations/barangay`, { web_filter: filter });
  }

  /**
   * Register or update a patient along with prescription request
   * @param request Upsert request containing registration_code/url/registration_mode and payload
   * @returns Observable with registration response
   */
  registerPatient(request: UpsertRequest): Observable<RegistrationResponse> {
    const payload = {
      ...request,
      registration_code: this.patientService.getRegistrationCode()
    };
    return this.http.post<RegistrationResponse>(`${this.apiUrl}/paacap-patient-reg/upsert`, payload);
  }

  /**
   * Add a new prescription request for an existing patient
   * @param payload Prescription request data (patient_id derived from authentication context)
   * @returns Observable with request response
   */
  addRequest(payload: any): Observable<RegistrationResponse> {
    const requestPayload = {
      ...payload,
      registration_code: this.patientService.getRegistrationCode()
    };
    return this.http.post<RegistrationResponse>(`${this.apiUrl}/paacap-patient-reg/add_request`, requestPayload);
  }

  /**
   * Obtain a presigned S3 url for uploading a file.
   * @param fileName full path/key for the object in S3 (e.g. uploads/prescription/123.jpg)
   * @param contentType MIME type of the file
   */
  getPresignedUrl(fileName: string, contentType: string): Observable<any> {
    // backend route is namespaced under paacap-patient-reg
    return this.http.post<any>(`${this.apiUrl}/paacap-patient-reg/image_upload`, {
      data: {
        file_name: fileName,
        content_type: contentType
      }
    });
  }

  /**
   * Uploads a file directly to the provided presigned URL.
   * Returns an observable that resolves when the PUT completes.
   */
  uploadFileToS3(presignedUrl: string, file: File): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': file.type });
    // using HttpClient directly because HttpService does not expose responseType options
    return this.httpClient.put(presignedUrl, file, { headers, responseType: 'text' });
  }
}